import { Button } from '@/components/ui/button';
import { Modal } from './modal';
import { DialogClose } from '@/components/ui/dialog';
import { Trash2, AlertTriangle } from 'lucide-react';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useEffect, useState } from 'react';
import { getUploadFileTypes } from '@/services/data-sources/get-upload-file-types';

interface DeleteCollectionModalProps {
  isLoading?: boolean;
  isOpen: boolean;
  handleSoftDelete: () => void;
  handleHardDelete: () => void;
  handleCancel: () => void;
}

export const DeleteCollectionModal = ({
  handleSoftDelete,
  handleHardDelete,
  handleCancel,
  isLoading = false,
  isOpen = false,
}: DeleteCollectionModalProps) => {
  const [ttlValue, setTtlValue] = useState<number>(7);
  const [ttlUnit, setTtlUnit] = useState<string>('days');

  useEffect(() => {
    const fetchTtl = async () => {
      try {
        const response = await getUploadFileTypes();
        if (
          response?.data?.soft_delete_ttl_value &&
          response?.data?.soft_delete_ttl_unit
        ) {
          setTtlValue(response.data.soft_delete_ttl_value);
          setTtlUnit(response.data.soft_delete_ttl_unit);
        }
      } catch (error) {
        console.error('Failed to fetch soft delete TTL:', error);
        // Keep default values on error
      }
    };

    if (isOpen) {
      fetchTtl();
    }
  }, [isOpen]);
  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button
          id='delete-modal-cancel'
          onClick={handleCancel}
          variant='outline'
        >
          Cancel
        </Button>
      </DialogClose>
      <Button
        id='delete-modal-soft'
        onClick={handleSoftDelete}
        disabled={isLoading}
        variant='default'
      >
        {isLoading ? (
          <>
            <LoadingSpinner /> Mark for deletion
          </>
        ) : (
          'Mark for deletion'
        )}
      </Button>
      <Button
        id='delete-modal-hard'
        onClick={handleHardDelete}
        disabled={isLoading}
        variant='destructive'
      >
        {isLoading ? (
          <>
            <LoadingSpinner /> Delete immediately
          </>
        ) : (
          <>
            <AlertTriangle className='h-4 w-4 mr-1' />
            Delete immediately
          </>
        )}
      </Button>
    </>
  );

  const Title = () => (
    <div className='flex items-center gap-2'>
      <Trash2 />
      Delete collection
    </div>
  );

  const Description = () => {
    const timeText = `${ttlValue} ${ttlUnit}`;
    // Remove the 's' from plural units for the hyphenated form (e.g., "7-day" not "7-days")
    const periodUnit = ttlUnit.endsWith('s') ? ttlUnit.slice(0, -1) : ttlUnit;
    const periodText = `${ttlValue}-${periodUnit}`;

    return (
      <div className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          Choose how you want to delete this collection:
        </p>
        <div className='space-y-3'>
          <div className='rounded-md border border-border bg-muted/30 p-3'>
            <h4 className='font-medium text-sm mb-1'>Mark for deletion</h4>
            <p className='text-xs text-muted-foreground'>
              The collection will be scheduled for deletion and permanently
              removed in {timeText}. You can cancel this action during the{' '}
              {periodText} period.
            </p>
          </div>
          <div className='rounded-md border border-destructive/50 bg-destructive/5 p-3'>
            <div className='flex items-start gap-2'>
              <AlertTriangle className='h-4 w-4 text-destructive mt-0.5' />
              <div>
                <h4 className='font-medium text-sm mb-1 text-destructive'>
                  Delete immediately
                </h4>
                <p className='text-xs text-muted-foreground'>
                  The collection, all files, and Qdrant data will be permanently
                  deleted immediately. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isDialogOpen={isOpen}
      footer={<Footer />}
      title={<Title />}
      description={<Description />}
      setIsDialogOpen={handleCancel}
    />
  );
};
